import { createContext, type ReactNode, useContext, useMemo, useState } from "react";

export type ModalStore = {
    modalStack: string[];
    fullPageModalStack: string[];
    topModal: string | null;
    topFullPageModal: string | null;
    stackDepth: number;
    openFullPage(id: string): void;
    closeFullPage(id: string): void;
    openPartial(id: string): void;
    closePartial(id: string): void;
};

export const ModalContext = createContext<ModalStore>({
    modalStack: [],
    fullPageModalStack: [],
    topModal: null,
    topFullPageModal: null,
    stackDepth: 0,
    openFullPage(_id: string) {
        return;
    },
    closeFullPage(_id: string) {
        return;
    },
    openPartial(_id: string) {
        return;
    },
    closePartial(_id: string) {
        return;
    },
});

export function useModal() {
    const value = useContext(ModalContext);
    if (!value) {
        throw new Error("useModal must be wrapped in a <ModalProvider />");
    }
    return value;
}

export function ModalProvider({ children }: { children: ReactNode }) {
    const [modalStack, setModalStack] = useState<string[]>([]);
    const [fullPageModalStack, setFullPageModalStack] = useState<string[]>([]);

    const openPartial = (id: string) => {
        setModalStack([...modalStack, id].filter((v, i, a) => i === a.indexOf(v)));
    };
    const openFullPage = (id: string) => {
        setFullPageModalStack([...fullPageModalStack, id].filter((v, i, a) => i === a.indexOf(v)));
        openPartial(id);
    };

    const closePartial = (id: string) => {
        setModalStack(modalStack.filter((v) => v !== id));
    };
    const closeFullPage = (id: string) => {
        setFullPageModalStack(fullPageModalStack.filter((v) => v !== id));
        closePartial(id);
    };
    const stackDepth = useMemo(() => fullPageModalStack.length, [fullPageModalStack]);
    const topModal = useMemo(() => modalStack[modalStack.length - 1] ?? null, [modalStack]);
    const topFullPageModal = useMemo(
        () => fullPageModalStack[fullPageModalStack.length - 1] ?? null,
        [fullPageModalStack],
    );

    const value = {
        modalStack,
        fullPageModalStack,
        topModal,
        topFullPageModal,
        stackDepth,
        openFullPage,
        closeFullPage,
        openPartial,
        closePartial,
    };

    return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
}
